import { useState } from 'react';
import type { ReactNode } from 'react';
import Observer from '@researchgate/react-intersection-observer';
import loadable from '@loadable/component';
import { useTracking } from 'react-tracking';

import { Grid, Text } from '@simplywallst/ui-core';

import type { Company } from './StockTable';
import { PAGE_SIZE } from '../../query/fetchPage';
import { CellLink } from '../DataTable';

import Tooltip from '@/components/Tooltip';
import PNV from '@/components/PositiveNegativeValue';

import { ROUTE_STOCKS_MARKET_INDUSTRY } from '@/constants/routes';
import type { PrimaryCountryISOElseGlobal } from '@/constants/countries';

import {
  CompanyName,
  IndustryColumn,
  NameColumn,
  PELabel,
  SnowflakeColumn,
  ToolTipText,
  ValueColumn,
  WatchlistColumn,
} from './styled';
import { Badge } from '../Badge';

const Snowflake = loadable(() => import('./Snowflake'), {
  resolveComponent: (components) => components.Snowflake,
});
const GrowthTooltip = loadable(() => import('./ValuationRatio'), {
  resolveComponent: (components) => components.GrowthTooltip,
});
const ValuationRatio = loadable(() => import('./ValuationRatio'), {
  resolveComponent: (components) => components.ValuationRatio,
});
const WatchlistAddRemoveCompanyToggle = loadable(
  () => import('@/components/WatchlistAddRemoveCompanyToggle'),
  { ssr: false }
);

interface SSRLinkProps {
  url: string;
  children?: ReactNode;
}
export const SSRLink = ({ url, children }: SSRLinkProps) => (
  <CellLink
    as={RUNTIME_ENV === 'server' ? 'span' : undefined}
    to={RUNTIME_ENV === 'server' ? undefined : url}
  >
    {children}
  </CellLink>
);

interface Props {
  company: Company;
  index: number;
  isSticky: boolean;
  delayedCallback: (id: string) => void;
  cancelCallback: () => void;
  muteEvent: (e: any) => void;
  industry: string | 'any';
  market: PrimaryCountryISOElseGlobal;
  shortTermReturn: number | null;
  shortTermReturnFormatted: string;
  hideIndustryCol?: boolean;
}

export const Row = ({
  company: {
    companyId,
    uniqueSymbol,
    snowflakePoints,
    canonicalUrl,
    tickerSymbol,
    shortDescription,
    shortName,
    sharePrice,
    sharePriceFormatted,
    return1YrAbs,
    return1YrAbsFormatted,
    marketCap,
    marketCapFormatted,
    analystsPriceTarget,
    analystsPriceTargetFormatted,
    bestValuationRatio,
    peFormatted,
    pbFormatted,
    psFormatted,
    earningsGrowth,
    salesGrowth,
    analystCount,
    earningsGrowthFormatted,
    salesGrowthFormatted,
    dividendYield,
    dividendYieldFormatted,
    industrySlug,
    industryLabel,
    isRecentSearchEntry,
  },
  shortTermReturn,
  shortTermReturnFormatted,
  muteEvent,
  index,
  isSticky,
  industry,
  market,
  hideIndustryCol,
  delayedCallback,
  cancelCallback,
}: Props) => {
  const { trackEvent } = useTracking();
  const [isVisible, setIsVisible] = useState(index <= PAGE_SIZE - 1); // First page is always displayed for SSR
  const tableColSpan = industry === 'any' && !hideIndustryCol ? 12 : 11;
  return (
    <Observer
      threshold={[0, 1]}
      onChange={({ isIntersecting }) => {
        if (!isVisible && isIntersecting) {
          setIsVisible(true);
        }
      }}
    >
      <tr
        data-cy-id="stocks-table-row"
        onMouseOver={() => delayedCallback(companyId)}
        onMouseOut={cancelCallback}
        onFocus={() => delayedCallback(companyId)}
        onBlur={cancelCallback}
        onClick={() =>
          trackEvent({
            action: 'click',
            subject: 'table-row',
            uniqueSymbol: uniqueSymbol,
            companyId: companyId,
            index,
          })
        }
      >
        {isVisible ? (
          <>
            <SnowflakeColumn>
              <Grid placeContent="center" paddingLeft="x1" paddingLeftLg="x0">
                <Snowflake points={snowflakePoints} />
              </Grid>
            </SnowflakeColumn>
            <NameColumn isSticky={isSticky}>
              <CellLink to={canonicalUrl}>
                <Grid templateColumns="auto auto" gap="4px" alignItems="center">
                  <Text color="brand01" typography="xSmallStrong">
                    {tickerSymbol}
                  </Text>
                  {isRecentSearchEntry && (
                    <Grid justifyContent="flex-start" display="inline-grid">
                      <Badge>New</Badge>
                    </Grid>
                  )}
                </Grid>
                <Tooltip
                  title={<ToolTipText>{shortDescription}</ToolTipText>}
                  key={`table-tooltip-description${companyId}`}
                  light
                  zIndex={99999}
                >
                  <CompanyName onTouchEnd={muteEvent} onClick={muteEvent}>
                    {shortName}
                  </CompanyName>
                </Tooltip>
              </CellLink>
            </NameColumn>
            <ValueColumn value={sharePrice}>
              <SSRLink url={canonicalUrl}>{sharePriceFormatted}</SSRLink>
            </ValueColumn>
            <PNV
              as="td"
              value={shortTermReturn}
              data-cy-id="short-term-return-value"
            >
              <SSRLink url={canonicalUrl}>{shortTermReturnFormatted}</SSRLink>
            </PNV>
            <PNV as="td" value={return1YrAbs}>
              <SSRLink url={canonicalUrl}>{return1YrAbsFormatted}</SSRLink>
            </PNV>
            <ValueColumn value={marketCap}>
              <SSRLink url={canonicalUrl}>{marketCapFormatted}</SSRLink>
            </ValueColumn>
            <ValueColumn value={analystsPriceTarget}>
              <SSRLink url={canonicalUrl}>
                {analystsPriceTargetFormatted}
              </SSRLink>
            </ValueColumn>
            <ValueColumn value={bestValuationRatio.valuation}>
              <SSRLink url={canonicalUrl}>
                {!!bestValuationRatio.valuation && (
                  <Tooltip
                    key={`table-tooltip-${companyId}`}
                    title={
                      <ValuationRatio
                        priceToEarnings={peFormatted}
                        priceToBook={pbFormatted}
                        priceToSales={psFormatted}
                      />
                    }
                    light
                    zIndex={99999}
                  >
                    <PELabel onTouchEnd={muteEvent} onClick={muteEvent}>
                      {bestValuationRatio.valuationType}
                    </PELabel>
                  </Tooltip>
                )}
                {bestValuationRatio.valuationFormatted}
              </SSRLink>
            </ValueColumn>
            <ValueColumn value={earningsGrowth || salesGrowth}>
              <SSRLink url={canonicalUrl}>
                {(earningsGrowth || salesGrowth) && (
                  <Tooltip
                    key={`table-tooltip-${companyId}`}
                    title={
                      <GrowthTooltip
                        analystCount={analystCount}
                        earningsGrowth={earningsGrowthFormatted}
                        salesGrowth={salesGrowthFormatted}
                      />
                    }
                    light
                    zIndex={99999}
                  >
                    <PELabel onTouchEnd={muteEvent} onClick={muteEvent}>
                      {earningsGrowth ? 'E' : 'S'}
                    </PELabel>
                  </Tooltip>
                )}
                {earningsGrowth
                  ? earningsGrowthFormatted
                  : salesGrowthFormatted}
              </SSRLink>
            </ValueColumn>
            <ValueColumn value={dividendYield}>
              <SSRLink url={canonicalUrl}>{dividendYieldFormatted}</SSRLink>
            </ValueColumn>
            {industry === 'any' && !hideIndustryCol && (
              <IndustryColumn>
                <CellLink
                  to={{
                    type: ROUTE_STOCKS_MARKET_INDUSTRY,
                    payload: {
                      market: market,
                      industry: industrySlug,
                    },
                  }}
                  onClick={(e) => {
                    e.stopPropagation(); // prevent doubling up on the tracking event on the <tr/>
                    trackEvent({
                      action: 'click',
                      subject: 'table-row-industry-column',
                      uniqueSymbol: uniqueSymbol,
                      companyId: companyId,
                      index,
                    });
                  }}
                >
                  <span>{industryLabel}</span>
                </CellLink>
              </IndustryColumn>
            )}
            <WatchlistColumn>
              <WatchlistAddRemoveCompanyToggle
                companyId={companyId}
                tickerSymbol={tickerSymbol}
                styleType="gentle"
                minimal
              />
            </WatchlistColumn>
          </>
        ) : (
          <td colSpan={tableColSpan} />
        )}
      </tr>
    </Observer>
  );
};
